import React from "react";
import debounce from "lodash.debounce";
import parse from "html-react-parser";
import { Style } from "gc-types";

import { CatalogueEvents, MainWebsiteEvents } from "gc-types/generated/ipcBusEvents";
import { FilterRequestBody, ServerGateway } from "./ServerGateway";
import { FilterContext } from "./FilterContext";
import { Analytics, getStyleItemsAnalyticsData } from "./Analytics";
import {
  AttributeFilter,
  CatalogueReviews,
  CatalogueScopeFilters,
  CollectionDesignation,
  CollectionFilter,
  InvalidStyle,
  SearchAppResult,
  ResultFilters
} from "../common/interfaces";
import { StyleItems } from "../common/components/StyleItems";
import {
  createFiltersFromLocation,
  createQueryFromFilters,
  createURLHashFromFilters,
  createURLHashFromPaginationData
} from "../common/filterQueryUtils";
import { compareAttributeFilterOptions, coreLibraryInitialised } from "../common/util";
import {
  AvailabilityFilter,
  AvailabilityFilterContainer,
  CatalogueHeader,
  CataloguePagination,
  CataloguePaginationData,
  CatalogueReviewComponent,
  DesignationTabs,
  LoadSpinner,
  SortingBy,
  SortingDropdown
} from "./components";
import styleTags from "../common/styleTags";
import { AttributeFiltersContainer } from "./containers/AttributeFiltersContainer";
import { STYLE_PRICE_ATTRIBUTE_FILTER } from "../common/attributeFilterConstants";
import { isClearanceCatalogue } from "./util";
import { storedAvailableNow, storedCollectionDesignation, storedDates, storedPostcode } from "./storageServices";

export interface CatalogueProps {
  styles: Style[];
  invalidStyles: InvalidStyle[];
  catalogueScopeFilters: CatalogueScopeFilters;
  availableAttributeFilters: AttributeFilter[];
  appliedAttributeFilters?: AttributeFilter[];
  pathname: string;
  heading?: string;
  sorting?: SortingBy;
  description?: string;
  shortDescription?: string;
  lazyLoadAfterNImages?: number;
  paginationData?: CataloguePaginationData;
  reviews?: CatalogueReviews;
  availableOnDemandFilters?: ResultFilters;
  availableSubscriptionFilters?: ResultFilters;
  availableSaleFilters?: ResultFilters;
  useWebflowBanner: boolean;
}

export interface CatalogueState {
  firstRender: boolean;
  catalogueCurrentUrl: string;
  styles: Style[];
  invalidStyles: any[];
  collectionFilter: CollectionFilter;
  appliedAttributeFilters: AttributeFilter[];
  availabilityFilter: AvailabilityFilter;
  showFilter: boolean;
  heading: string;
  description?: string;
  paginationData: CataloguePaginationData;
  loadingStyles: boolean;
}

export class Catalogue extends React.Component<CatalogueProps, CatalogueState> {
  private readonly gateway = new ServerGateway();
  private readonly analytics = new Analytics();
  private readonly debouncedApplyFilters: Function;

  constructor(props: CatalogueProps) {
    super(props);

    const {
      styles,
      invalidStyles,
      heading,
      sorting,
      appliedAttributeFilters,
      paginationData,
      catalogueScopeFilters
    } = this.props;

    // If there's only one tab available, load that tab instead of All
    let initialDesignation: CollectionDesignation = CollectionDesignation.All;
    let initialAvailableNow = true;

    const onlySubscriptionDesignation =
      catalogueScopeFilters.availableDesignations.length === 1 &&
      catalogueScopeFilters.availableDesignations[0] === CollectionDesignation.Subscription;

    if (onlySubscriptionDesignation) {
      initialDesignation = catalogueScopeFilters.availableDesignations[0];
      initialAvailableNow = false;
    }

    this.state = {
      firstRender: true,
      catalogueCurrentUrl: "",
      showFilter: false,
      heading: heading || "Hire Designer Dresses",
      styles,
      invalidStyles,
      collectionFilter: {
        collectionDesignation: initialDesignation,
        sorting: sorting || SortingBy.Newest
      },
      appliedAttributeFilters: appliedAttributeFilters || [],
      availabilityFilter: {
        availableNow: initialAvailableNow,
        postcode: "",
        deliveryDates: {
          startDate: "",
          endDate: ""
        }
      },
      paginationData: paginationData || {
        offset: 0,
        limit: 24,
        total: styles.length
      },
      loadingStyles: false
    };

    this.debouncedApplyFilters = debounce(this.applyFilter, 200);
  }

  async setFiltersAndPaginationData() {
    const { availableAttributeFilters, catalogueScopeFilters } = this.props;
    let presetAvailableNow = await storedAvailableNow.get();
    let presetDesignation = await storedCollectionDesignation.get();
    const presetDates = await storedDates.get();
    const presetPostcode = await storedPostcode.get();
    let presetAvailabilityFilter: AvailabilityFilter = {
      ...this.state.availabilityFilter,
      ...(typeof presetAvailableNow === "boolean" && { availableNow: presetAvailableNow }),
      ...(presetPostcode && { postcode: presetPostcode }),
      ...(presetDates && {
        deliveryDates: { startDate: presetDates.startDate.toString(), endDate: presetDates.endDate.toString() }
      })
    };

    if (window.location.hash !== "" || window.location.search.includes("filters=")) {
      const {
        appliedAttributeFilters,
        availabilityFilter,
        collectionFilter,
        paginationData
      } = createFiltersFromLocation(window.location, availableAttributeFilters);
      presetDesignation = collectionFilter.collectionDesignation;

      const newAppliedAttributeFilters: AttributeFilter[] = [
        ...(this.state.appliedAttributeFilters || []),
        ...appliedAttributeFilters
      ].reduce((acc, filter) => {
        const result: AttributeFilter[] = acc.filter((f: AttributeFilter) => filter.code !== f.code);
        result.push(filter);
        return result;
      }, []);

      presetAvailabilityFilter = {
        ...presetAvailabilityFilter,
        ...availabilityFilter
      };

      this.setState({
        paginationData,
        collectionFilter,
        appliedAttributeFilters: newAppliedAttributeFilters
      });
    }

    const availableCollectionDesignations = catalogueScopeFilters.availableDesignations;
    let collectionDesignation: CollectionDesignation = availableCollectionDesignations.includes(presetDesignation)
      ? presetDesignation
      : availableCollectionDesignations[0];

    this.setState({
      availabilityFilter: presetAvailabilityFilter,
      collectionFilter: {
        ...this.state.collectionFilter,
        collectionDesignation
      }
    });
  }

  async triggerStateUpdateOnPathChange() {
    if (this.state.catalogueCurrentUrl !== `${window.location.href}`) {
      await this.setFiltersAndPaginationData();
    }
  }

  async componentDidMount() {
    this.setState({
      appliedAttributeFilters:
        this.state.appliedAttributeFilters.length === 0
          ? this.props.appliedAttributeFilters || []
          : this.state.appliedAttributeFilters
    });

    const { searchText } = this.props.catalogueScopeFilters;

    window.addEventListener("popstate", this.triggerStateUpdateOnPathChange.bind(this));

    // @ts-ignore
    if (!("ontouchstart" in document.documentElement)) {
      // Works but Typescript complains about documentElement being type Never
      // @ts-ignore
      document.documentElement.classList.add("no-touch");
    }

    if (coreLibraryInitialised() && searchText) {
      (global as any).GlamCornerCore.bus.broadcast(CatalogueEvents.StyleItemsSearched, {
        searchText
      });
    }

    await this.setFiltersAndPaginationData();
  }

  componentWillUnmount() {
    window.removeEventListener("popstate", this.triggerStateUpdateOnPathChange.bind(this));
  }

  componentDidUpdate(prevProps: Readonly<CatalogueProps>, prevState: Readonly<CatalogueState>, snapshot?: any): void {
    if (this.shouldApplyFilters(prevState, this.state)) {
      this.debouncedApplyFilters();
    }
    if (coreLibraryInitialised()) {
      (global as any).GlamCornerCore.bus.broadcast(MainWebsiteEvents.CatalogueLoaded, {});
    }
  }

  render() {
    const {
      styles,
      invalidStyles,
      appliedAttributeFilters,
      collectionFilter,
      availabilityFilter,
      showFilter,
      paginationData,
      loadingStyles
    } = this.state;
    const {
      pathname,
      heading,
      description,
      shortDescription,
      availableAttributeFilters,
      lazyLoadAfterNImages,
      catalogueScopeFilters,
      reviews,
      availableOnDemandFilters,
      availableSubscriptionFilters,
      availableSaleFilters
    } = this.props;
    const hasStyles = this.state.styles.length > 0;
    const hasInvalidStyles = this.state.invalidStyles.length > 0;
    const catalogueSearchMode = !!catalogueScopeFilters.searchText;
    const hashUrl = createURLHashFromFilters(collectionFilter, availabilityFilter, appliedAttributeFilters);

    const showHeader = !this.props.useWebflowBanner;

    return (
      <FilterContext.Provider
        value={{
          availableAttributeFilters,
          availableOnDemandFilters,
          availableSubscriptionFilters,
          availableSaleFilters,
          appliedAttributeFilters,
          collectionFilter,
          availabilityFilter,
          clearAvailabilityFilters: this.handleClearAvailabilityFilters,
          clearAppliedAttributeFilters: this.handleClearAttributeFilters,
          applyAttributeFilter: this.handleApplyAttributeFilter,
          applyCollectionFilter: this.handleApplyCollectionFilter,
          applyAvailabilityFilter: this.handleApplyAvailabilityFilter
        }}
      >
        <div className={`c-catalogue container relative ${showHeader ? "" : "-no-show-header"}`}>
          <div
            className={`c-catalogue__attribute-filters ${showFilter ? "c-catalogue__attribute-filters--shown" : ""}`}
          >
            <AttributeFiltersContainer attributeFilters={availableAttributeFilters} />
          </div>
          <div className={`c-catalogue__main${showFilter ? "--hidden" : ""}`}>
            <div className="u-reverse-on-desktop">
              <div>
                {loadingStyles && <LoadSpinner useOldStyle={true} />}
                <DesignationTabs
                  value={collectionFilter.collectionDesignation}
                  tabs={catalogueScopeFilters.availableDesignations}
                  cataloguePathname={pathname}
                  filters={{ collectionFilter, availabilityFilter, appliedAttributeFilters }}
                  paginationData={paginationData}
                />
                <AvailabilityFilterContainer />
              </div>
              {catalogueSearchMode ? (
                <div className="c-catalogue__search-page-title">
                  Search results for: '{catalogueScopeFilters.searchText}'
                </div>
              ) : (
                showHeader && (
                  <CatalogueHeader
                    title={heading}
                    count={paginationData.total}
                    shortDescription={shortDescription && this.unescapeHTML(shortDescription)}
                  />
                )
              )}
            </div>
            {!catalogueSearchMode && (
              <SortingDropdown
                value={collectionFilter.sorting}
                onSelected={(sorting: SortingBy) => this.handleApplyCollectionFilter({ sorting })}
                collectionDesignation={collectionFilter.collectionDesignation}
              />
            )}
            {hasInvalidStyles ? (
              <>
                <div style={{ display: "none" }}>
                  Invalid styles: {invalidStyles.map(s => s.style.style_code).join(",")}
                </div>
              </>
            ) : null}
            {hasStyles ? (
              <>
                <StyleItems
                  hashUrl={hashUrl}
                  styles={styles}
                  totalCount={styles.length}
                  lazyLoadAfterImages={lazyLoadAfterNImages || 6}
                  tag={styleTags}
                  camelCaseMicroData={true}
                  withStyleImageCarousels={false}
                  collectionDesignation={collectionFilter.collectionDesignation}
                />
                <div className="toolbar-bottom toolbar">
                  <div className="toolbar">
                    <CataloguePagination
                      data={paginationData}
                      onClick={(pData: CataloguePaginationData) => {
                        if (this.state.paginationData.offset !== pData.offset) {
                          this.scrollToTop();
                        }

                        this.setState({ paginationData: pData });
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p className="no-results-message" style={{ textAlign: "center" }}>
                We couldn't find anything matching your search criteria. Please remove some filters and try again.
              </p>
            )}

            {description && (
              <div className="c-category-description t-catalog-description js-catalog-description std">
                {parse(this.unescapeHTML(description))}
              </div>
            )}

            {reviews && <CatalogueReviewComponent reviews={reviews} heading={heading} />}
          </div>
        </div>
        <div className="c-catalogue__fab" data-mode={showFilter ? "done" : "refine"}>
          <button
            onClick={() => this.handleFabClick()}
            type="button"
            className="gc-button gc-button--small gc-button--inline"
          >
            {showFilter ? "Done" : "Refine"}
          </button>
        </div>
      </FilterContext.Provider>
    );
  }

  private async applyFilter() {
    this.setState({ loadingStyles: true });
    const { catalogueScopeFilters } = this.props;
    const { collectionFilter, availabilityFilter, appliedAttributeFilters, paginationData } = this.state;

    this.analytics.broadcastFilterEvent({
      attributeFilters: appliedAttributeFilters,
      collectionFilter,
      availabilityFilter
    });
    storedCollectionDesignation.set(collectionFilter.collectionDesignation);

    const catalogueSearchQuery = createQueryFromFilters({
      catalogueScopeFilters,
      collectionFilter,
      availabilityFilter,
      appliedAttributeFilters,
      paginationData
    });

    const newUrl = this.updateUrl();

    const requestBody: FilterRequestBody = {
      query: catalogueSearchQuery
    };
    if (catalogueScopeFilters.styleCodes) {
      requestBody.styleCodes = catalogueScopeFilters.styleCodes;
    }

    const response: SearchAppResult | null = await this.gateway.filterRequest(requestBody);

    if (response) {
      const styleItemsAnalyticsData = getStyleItemsAnalyticsData(
        response.data,
        isClearanceCatalogue(),
        collectionFilter,
        availabilityFilter,
        appliedAttributeFilters
      );

      if (coreLibraryInitialised() && styleItemsAnalyticsData.products && styleItemsAnalyticsData.products.length) {
        if (this.state.firstRender) {
          (global as any).GlamCornerCore.bus.broadcast(CatalogueEvents.StyleItemsViewed, {
            ...styleItemsAnalyticsData
          });
        } else {
          (global as any).GlamCornerCore.bus.broadcast(CatalogueEvents.StyleItemsFiltered, {
            ...styleItemsAnalyticsData
          });
        }
      }

      this.setState({
        catalogueCurrentUrl: newUrl,
        styles: response.data || [],
        paginationData: { ...this.state.paginationData, total: response.count },
        loadingStyles: false,
        firstRender: false
      });
    } else {
      this.setState({ loadingStyles: false, firstRender: false });
    }
  }

  // When we change filters, set the page back to 1
  private getResetPageState = () => {
    return { ...this.state.paginationData, offset: 0 } as CataloguePaginationData;
  };

  private handleClearAttributeFilters = () => {
    const nonClearableFilters = ["size"];
    const clearedAttributes = this.state.appliedAttributeFilters.filter(f => nonClearableFilters.includes(f.code));
    this.setState(
      {
        appliedAttributeFilters: clearedAttributes,
        showFilter: false,
        paginationData: this.getResetPageState()
      },
      this.toggleMobilePageFooterVisibility
    );
  };

  private handleClearAvailabilityFilters = () => {
    const emptyAvailabilityFilters: AvailabilityFilter = {
      postcode: "",
      availableNow: true,
      deliveryDates: {
        endDate: "",
        startDate: ""
      }
    };
    const attributeFiltersWithoutSizes: AttributeFilter[] = this.state.appliedAttributeFilters.filter(
      f => f.code !== "size"
    );
    this.setState({
      availabilityFilter: emptyAvailabilityFilters,
      appliedAttributeFilters: attributeFiltersWithoutSizes,
      paginationData: this.getResetPageState()
    });

    storedAvailableNow.clear();
    storedDates.clear();
    storedPostcode.clear();
  };

  private handleApplyAttributeFilter = (filter: AttributeFilter) => {
    const filterIndex = this.state.appliedAttributeFilters.findIndex(f => f.code === filter.code);

    const updatedAppliedAttributeFilters = [...this.state.appliedAttributeFilters];
    if (filterIndex > -1) {
      updatedAppliedAttributeFilters[filterIndex] = filter;
    } else {
      updatedAppliedAttributeFilters.push(filter);
    }

    this.setState({
      appliedAttributeFilters: updatedAppliedAttributeFilters,
      paginationData: this.getResetPageState()
    });
  };

  private handleApplyCollectionFilter = (collectionFilter: Partial<CollectionFilter>) => {
    const { appliedAttributeFilters } = this.state;
    this.setState({
      appliedAttributeFilters: this.isSameDesignation(collectionFilter)
        ? appliedAttributeFilters
        : this.removePriceFilter(),
      collectionFilter: {
        ...this.state.collectionFilter,
        ...collectionFilter
      },
      paginationData: this.getResetPageState()
    });
  };

  private isSameDesignation = ({ collectionDesignation }: Partial<CollectionFilter>) => {
    const { collectionFilter } = this.state;
    return (
      !collectionDesignation ||
      (collectionDesignation && collectionDesignation === collectionFilter.collectionDesignation)
    );
  };

  private handleApplyAvailabilityFilter = (availabilityFilter: Partial<AvailabilityFilter>) => {
    const updatedAvailabilityFilterState = {
      ...this.state.availabilityFilter,
      ...availabilityFilter
    };
    this.setState({
      availabilityFilter: updatedAvailabilityFilterState,
      paginationData: this.getResetPageState()
    });

    storedAvailableNow.set(updatedAvailabilityFilterState.availableNow);
    storedPostcode.set(updatedAvailabilityFilterState.postcode);
    storedDates.set(updatedAvailabilityFilterState.deliveryDates);
  };

  private handleFabClick() {
    this.setState(
      {
        showFilter: !this.state.showFilter
      },
      () => {
        this.toggleMobilePageFooterVisibility();
        this.scrollToTop();
      }
    );
  }

  private toggleMobilePageFooterVisibility = () => {
    const footerEl = document.querySelector(".page > .footer");
    if (footerEl) {
      footerEl.classList.toggle("l-m-hidden");
    }
  };

  private unescapeHTML = (unsafe: string) => {
    return unsafe
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, `"`)
      .replace(/&#039;/g, "'")
      .replace(/%24/g, "$");
  };

  private updateUrl(): string {
    const { collectionFilter, availabilityFilter, appliedAttributeFilters, paginationData } = this.state;

    const filtersHash = createURLHashFromFilters(collectionFilter, availabilityFilter, appliedAttributeFilters);
    const paginationHash = createURLHashFromPaginationData(paginationData);
    const urlHash = `#${filtersHash}&${paginationHash}`;

    if (window.location.hash !== `${urlHash}`) {
      let pageNumber = paginationData.offset / paginationData.limit;
      let paginationParam = "";
      if (pageNumber > 0) {
        pageNumber = pageNumber + 1;
        paginationParam = `?p=${pageNumber}`;
      }
      window.history.pushState({}, document.title, `${window.location.pathname}${paginationParam}${urlHash}`);

      const canonicalLink = window.document.querySelector("link[rel='canonical']");
      const canonicalLinkHref = canonicalLink?.getAttribute("href");
      if (canonicalLink && canonicalLinkHref) {
        const url = new URL(canonicalLinkHref);

        if (pageNumber === 0) {
          url.searchParams.delete("p");
        } else {
          url.searchParams.set("p", pageNumber.toString());
        }

        canonicalLink.setAttribute("href", url.toString());
      }
    }

    return `${window.location.href}`;
  }

  private scrollToTop = () => window.scrollTo(0, 0);

  // Only submit new request of any of the filters changed.
  // Only compare paginationData.offset because it has total count, which changes when applyFilter gets applied
  private shouldApplyFilters = (prevState: Readonly<CatalogueState>, thisState: Readonly<CatalogueState>): boolean => {
    const prevAppliedFilters = JSON.stringify([
      prevState.appliedAttributeFilters.map(af => af.options.sort(compareAttributeFilterOptions)),
      prevState.collectionFilter,
      prevState.availabilityFilter,
      prevState.paginationData.offset
    ]);
    const currentAppliedFilters = JSON.stringify([
      thisState.appliedAttributeFilters.map(af => af.options.sort(compareAttributeFilterOptions)),
      thisState.collectionFilter,
      thisState.availabilityFilter,
      thisState.paginationData.offset
    ]);

    return currentAppliedFilters !== prevAppliedFilters;
  };

  private removePriceFilter = () => {
    const { appliedAttributeFilters } = this.state;
    return appliedAttributeFilters.filter(f => f.code !== STYLE_PRICE_ATTRIBUTE_FILTER.code);
  };
}
